/**
 * Expandable Activity Card
 *
 * Click-to-expand timeline card. All cards show full details when expanded.
 * Issue cards (entity_type === "container_issue") additionally fetch and show
 * issue detail + inline action buttons.
 *
 * Non-issue attention cards show a "Mark as reviewed" button instead of
 * auto-marking as read on expand.
 *
 * All attention cards (issue + non-issue) show a "Send email" button that
 * opens a modal to send an alert email about the activity.
 */

import { useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
    Mail01,
    File06,
    MarkerPin01,
    AlertCircle,
    Zap,
    MessageChatSquare,
    MessageTextSquare01,
    Edit05,
    Package,
    User01,
    ChevronDown,
    CheckCircle,
    Send01,
} from '@untitledui/icons';
import type { BackendActivity } from '@/data/api/activities';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { useMarkActivityAsRead, useMarkActivityAsUnread } from '@/hooks/api/use-activities';
import { useIssueDetail } from '@/hooks/api/use-issue-detail';
import { IssueDetailSection } from './issue-detail-section';
import { InlineIssueActions } from './inline-issue-actions';
import { SendActivityEmailModal } from './send-activity-email-modal';
import { stripUuids } from '@/utils/strip-uuids';
import { getCategoryLabel, getActorDisplayName } from '@/utils/activity-labels';
import { generateFlaggedEmailTitle } from '@/utils/activity-mapper';

// ============================================================================
// Helpers (shared with container-activity-timeline.tsx)
// ============================================================================

const getCategoryConfig = (category: string) => {
    const configs: Record<string, { icon: typeof Mail01; color: string; bgColor: string }> = {
        document: { icon: File06, color: 'text-success-600', bgColor: 'bg-success-100' },
        tracking: { icon: MarkerPin01, color: 'text-warning-600', bgColor: 'bg-warning-100' },
        contact: { icon: User01, color: 'text-brand-600', bgColor: 'bg-brand-100' },
        agent: { icon: Zap, color: 'text-purple-600', bgColor: 'bg-purple-100' },
        // Legacy categories
        email: { icon: Mail01, color: 'text-purple-600', bgColor: 'bg-purple-100' },
        issue: { icon: AlertCircle, color: 'text-success-600', bgColor: 'bg-success-100' },
        notification: { icon: AlertCircle, color: 'text-error-600', bgColor: 'bg-error-100' },
        system: { icon: Package, color: 'text-gray-600', bgColor: 'bg-gray-100' },
    };
    return configs[category] || { icon: MessageChatSquare, color: 'text-tertiary', bgColor: 'bg-secondary' };
};

const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ============================================================================
// Component
// ============================================================================

interface ExpandableActivityCardProps {
    activity: BackendActivity;
    containerId?: string;
    /** If true, hides "Vedi Container" button (we're already on that container's page) */
    isContainerContext?: boolean;
    /** Compact layout for Control Hub (smaller icon, text, padding) */
    compact?: boolean;
}

export function ExpandableActivityCard({ activity, containerId, isContainerContext, compact }: ExpandableActivityCardProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [markedRead, setMarkedRead] = useState(false);
    const isUnread = !activity.is_read && !markedRead;

    const isIssue = activity.entity_type === 'container_issue';
    const issueId = isIssue ? activity.entity_id : undefined;
    const isNonIssueAttention = !isIssue && activity.attention_level === 'require_review';
    const showSendEmail = activity.attention_level !== null || isIssue;

    const markAsRead = useMarkActivityAsRead();
    const markAsUnread = useMarkActivityAsUnread();
    // Fetch issue detail for all issue cards (needed for badge + expanded view)
    const { data: issueDetail, isLoading: issueLoading } = useIssueDetail(issueId, {
        enabled: isIssue,
    });

    const isActionDone = isIssue && issueDetail != null &&
        !['open'].includes(issueDetail.resolution_status);

    const config = getCategoryConfig(activity.activity_category);
    const Icon = config.icon;

    const handleToggle = useCallback(() => {
        const willExpand = !isExpanded;
        setIsExpanded(willExpand);

        // Auto-mark as read on expand for non-attention cards only.
        // Attention cards keep their unread dot until explicit "Mark as reviewed".
        if (willExpand && isUnread && !activity.attention_level) {
            markAsRead.mutate({ activityId: activity.activity_id });
            setMarkedRead(true);
        }
    }, [isExpanded, isUnread, activity.activity_id, markAsRead, activity.attention_level]);

    const handleMarkAsReviewed = useCallback(() => {
        markAsRead.mutate({ activityId: activity.activity_id });
        setMarkedRead(true);
    }, [activity.activity_id, markAsRead]);

    const handleMarkAsUnreviewed = useCallback(() => {
        markAsUnread.mutate({ activityId: activity.activity_id });
        setMarkedRead(false);
    }, [activity.activity_id, markAsUnread]);

    // Badge logic
    const issueSeverity = (activity.details?.severity as string)?.toLowerCase();
    const isHighSeverity = issueSeverity === 'high' || issueSeverity === 'critical';

    const renderBadge = () => {
        if (isActionDone) {
            return <Badge color="success" size="sm">Action done</Badge>;
        }
        if (activity.attention_level === 'action_needed') {
            return <Badge color="warning" size="sm">Action Needed</Badge>;
        }
        if (activity.attention_level === 'require_review' && isUnread) {
            return <Badge color="blue" size="sm">Requires Review</Badge>;
        }
        if (activity.attention_level === 'require_review' && !isUnread) {
            return <Badge color="success" size="sm">Reviewed</Badge>;
        }
        return null;
    };

    return (
        <div className="relative min-w-0 max-w-full">
            {/* Clickable header — only this region toggles expand/collapse */}
            <div
                className="cursor-pointer select-none"
                onClick={handleToggle}
                role="button"
                tabIndex={0}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} activity: ${activity.title}`}
                aria-expanded={isExpanded}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle();
                    }
                }}
            >
                <div className={compact ? "flex gap-3" : "flex gap-4"}>
                    {/* Icon */}
                    <div
                        className={`relative z-10 flex shrink-0 items-center justify-center rounded-full ${config.bgColor} ring-4 ring-primary ${
                            compact ? 'h-8 w-8' : 'h-10 w-10'
                        }`}
                    >
                        <Icon className={compact ? `h-4 w-4 ${config.color}` : `h-5 w-5 ${config.color}`} />
                    </div>

                    {/* Content */}
                    <div className={`flex-1 min-w-0 ${compact ? 'pt-0.5' : 'pt-1'}`}>
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <p className={`font-medium text-primary line-clamp-2 ${compact ? 'text-xs' : 'text-sm'}`}>
                                    {(() => {
                                        let title = activity.activity_type === 'email_flagged'
                                            ? generateFlaggedEmailTitle(activity)
                                            : (activity.title ? stripUuids(activity.title) : '');
                                        // Strip container number from title when already in container context
                                        if (isContainerContext && activity.container_number) {
                                            title = title.replace(activity.container_number, '').replace(/:\s*$/, '').replace(/:\s*:/, ':').trim();
                                        }
                                        return title;
                                    })()}
                                </p>
                                <p className={`text-tertiary mt-0.5 ${compact ? 'text-xs' : 'text-sm'} ${isExpanded ? '' : 'line-clamp-2'}`}>
                                    {activity.summary ? stripUuids(activity.summary) : ''}
                                </p>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                                <div className="flex items-center gap-1.5">
                                    {isUnread && (
                                        <span className="h-2 w-2 rounded-full bg-brand-500" title="Unread" />
                                    )}
                                    <span className="text-xs text-tertiary whitespace-nowrap">
                                        {formatRelativeTime(activity.created_at)}
                                    </span>
                                </div>
                                {renderBadge()}
                            </div>
                        </div>

                        {/* Metadata */}
                        <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                            {/* Category badge — always shown */}
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${config.bgColor} ${config.color}`}>
                                {getCategoryLabel(activity.activity_category)}
                            </span>
                            {/* Actor name — only for agent/user, skip "Sistema" which adds no info */}
                            {(activity.actor_type === 'agent' || activity.actor_type === 'user') && (
                                <div className="flex items-center gap-1.5 text-xs text-tertiary">
                                    {activity.actor_type === 'agent' ? (
                                        <Zap className="h-3.5 w-3.5" />
                                    ) : (
                                        <User01 className="h-3.5 w-3.5" />
                                    )}
                                    <span>{getActorDisplayName(activity.actor_name, activity.source_type)}</span>
                                </div>
                            )}
                            {/* Issue type label — visible in closed state */}
                            {activity.entity_type === 'container_issue' && !!activity.details?.issue_type && (
                                <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-secondary text-secondary">
                                    {(() => {
                                        const issueType = String(activity.details.issue_type);
                                        // If cross-check issue but has missing_doc_types → it's really a missing doc issue
                                        const hasMissingDocs = Array.isArray(activity.details.missing_doc_types) && (activity.details.missing_doc_types as unknown[]).length > 0;
                                        if (issueType === 'cross_check_document_check' && hasMissingDocs) return 'Missing Document';
                                        const labels: Record<string, string> = {
                                            'missing_document': 'Missing Document',
                                            'single_document_check': 'Single Doc Check',
                                            'cross_check_document_check': 'Cross-Check',
                                            'documentation_query': 'Documentation Query',
                                            'carrier_delay': 'Carrier Delay',
                                            'temperature_excursion': 'Temperature',
                                            'customs_hold': 'Customs Hold',
                                        };
                                        return labels[issueType] || issueType.replace(/_/g, ' ');
                                    })()}
                                </span>
                            )}
                            {/* Issue severity tag — visible in closed state */}
                            {activity.entity_type === 'container_issue' && !!activity.details?.severity && (
                                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                    ['critical', 'high'].includes(String(activity.details.severity).toLowerCase())
                                        ? 'bg-warning-100 text-warning-700'
                                        : 'bg-blue-100 text-blue-700'
                                }`}>
                                    {['critical', 'high'].includes(String(activity.details.severity).toLowerCase())
                                        ? 'Action Needed'
                                        : 'Requires Review'}
                                </span>
                            )}
                            <motion.span animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }} className="ml-auto">
                                <ChevronDown className="h-4 w-4 text-tertiary" />
                            </motion.span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded section — outside the clickable header */}
            <AnimatePresence initial={false}>
                {isExpanded && (
                    <motion.div
                        key="expanded"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className="overflow-hidden"
                    >
                        <div className={compact ? "pl-11" : "pl-14"}>
                            {/* Attention reason */}
                            {activity.attention_reason && (
                                <p className="mt-2 text-xs text-warning-600">
                                    {activity.attention_reason}
                                </p>
                            )}

                            {/* Check type label */}
                            {isIssue && !!activity.details?.issue_type && (
                                ['single_document_check', 'cross_check_document_check', 'missing_document'].includes(String(activity.details.issue_type))
                            ) && (
                                <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-secondary text-xs font-medium text-primary">
                                    {(() => {
                                        const it = String(activity.details.issue_type);
                                        const hasMissingDocs = Array.isArray(activity.details.missing_doc_types) && (activity.details.missing_doc_types as unknown[]).length > 0;
                                        if (it === 'missing_document' || (it === 'cross_check_document_check' && hasMissingDocs)) return 'Missing Document';
                                        if (it === 'single_document_check') return 'Single Document Check';
                                        return 'Cross-Document Check';
                                    })()}
                                </div>
                            )}

                            {/* Rich metadata — extracted from activity.details */}
                            <div className="mt-3 space-y-2 text-xs text-secondary">
                                {/* Document info */}
                                {!!activity.details?.document_name && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-tertiary">Documento:</span>
                                        <span className="font-medium">{String(activity.details.document_name)}</span>
                                        {!!activity.details?.document_type && (
                                            <span className="text-tertiary">({String(activity.details.document_type).replace(/_/g, ' ')})</span>
                                        )}
                                    </div>
                                )}
                                {/* Source — enriched with email subject / document type when available */}
                                {!!activity.details?.source && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-tertiary">Fonte:</span>
                                        <span>{(() => {
                                            const src = String(activity.details.source);
                                            if (src === 'email' || src === 'email_extraction' || src === 'email_body') return 'Email';
                                            if (src === 'document_extraction') return 'Documento';
                                            if (src === 'manual') return 'Upload manuale';
                                            if (src === 'jsoncargo_sync') return 'Tracking provider';
                                            return src;
                                        })()}</span>
                                        {/* Email subject when source is email */}
                                        {!!activity.details?.source_email_subject && (
                                            <span className="text-tertiary">
                                                &mdash; {String(activity.details.source_email_subject)}
                                            </span>
                                        )}
                                        {/* Document type when source is document */}
                                        {String(activity.details.source) === 'document_extraction' && !!activity.details?.document_type && (
                                            <span className="text-tertiary">
                                                &mdash; {String(activity.details.document_type).replace(/_/g, ' ')}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {/* Email info */}
                                {!!(activity.details?.from || activity.details?.email_from) && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-tertiary">Da:</span>
                                        <span className="font-medium">{String(activity.details.from || activity.details.email_from)}</span>
                                    </div>
                                )}
                                {!!(activity.details?.subject || activity.details?.email_subject) && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-tertiary">Oggetto:</span>
                                        <span className="truncate">{String(activity.details.subject || activity.details.email_subject)}</span>
                                    </div>
                                )}
                                {/* Status change */}
                                {!!activity.details?.old_status && !!activity.details?.new_status && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-tertiary">Stato:</span>
                                        <span className="line-through text-tertiary">{String(activity.details.old_status).replace(/_/g, ' ')}</span>
                                        <span>&rarr;</span>
                                        <span className="font-medium">{String(activity.details.new_status).replace(/_/g, ' ')}</span>
                                    </div>
                                )}
                                {/* Delay info */}
                                {!!activity.details?.delay_hours && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-tertiary">Ritardo:</span>
                                        <span className="font-medium text-warning-600">{String(activity.details.delay_hours)}h</span>
                                    </div>
                                )}
                                {/* Contact info */}
                                {!!activity.details?.contact_name && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-tertiary">Contatto:</span>
                                        <span className="font-medium">{String(activity.details.contact_name)}</span>
                                        {!!activity.details?.contact_company_name && (
                                            <span className="text-tertiary">({String(activity.details.contact_company_name)})</span>
                                        )}
                                    </div>
                                )}
                                {/* Note content */}
                                {activity.activity_type === 'note_created' && (
                                    <>
                                        <div className="flex items-start gap-2">
                                            <span className="text-tertiary">Tipo nota:</span>
                                            <span className="font-medium">{String(activity.details?.note_type) === 'customer' ? 'Cliente' : 'Interna'}</span>
                                            {!!activity.details?.created_by && (
                                                <>
                                                    <span className="text-tertiary">da</span>
                                                    <span className="font-medium">{String(activity.details.created_by)}</span>
                                                </>
                                            )}
                                        </div>
                                        {!!activity.details?.note_content && (
                                            <div className="mt-1 rounded-md bg-secondary/50 px-3 py-2">
                                                <p className="text-sm text-primary whitespace-pre-wrap">{String(activity.details.note_content)}</p>
                                            </div>
                                        )}
                                    </>
                                )}
                                {/* Document modified — show field changes */}
                                {activity.activity_type === 'document_modified' && !!activity.details?.changes && (
                                    <div className="space-y-1">
                                        <span className="text-tertiary">Modifica:</span>
                                        {(() => {
                                            const changes = activity.details.changes as Record<string, unknown>;
                                            const fieldKey = String(changes.field_key ?? '');
                                            const oldVal = changes.old_value;
                                            const newVal = changes.new_value;
                                            return (
                                                <div className="flex items-center gap-2 pl-2">
                                                    <span className="font-medium">{fieldKey.replace(/_/g, ' ')}</span>
                                                    {oldVal != null && <span className="line-through text-tertiary">{String(oldVal)}</span>}
                                                    {oldVal != null && newVal != null && <span>&rarr;</span>}
                                                    {newVal != null && <span className="font-medium">{String(newVal)}</span>}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                                {/* Company info */}
                                {activity.activity_type === 'company_created' && !!activity.details?.company_name && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-tertiary">Azienda:</span>
                                        <span className="font-medium">{String(activity.details.company_name)}</span>
                                        {!!activity.details?.company_type && (
                                            <span className="text-tertiary">({String(activity.details.company_type).replace(/_/g, ' ')})</span>
                                        )}
                                    </div>
                                )}
                                {/* Container tracked — tracking provider info */}
                                {activity.activity_type === 'container_tracked' && !!activity.details?.tracking_provider && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-tertiary">Provider:</span>
                                        <span className="font-medium">{String(activity.details.tracking_provider)}</span>
                                    </div>
                                )}
                                {/* Container reference — only when not in container context */}
                                {!isContainerContext && activity.container_number && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-tertiary">Container:</span>
                                        <span className="font-medium font-mono text-xs">{activity.container_number}</span>
                                    </div>
                                )}
                                {/* Documents involved (for document validation issues) */}
                                {!!activity.details?.validation_failures && Array.isArray(activity.details.validation_failures) && (
                                    <div className="mt-2 space-y-1">
                                        <span className="text-tertiary">Documenti coinvolti:</span>
                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                            {(() => {
                                                const failures = activity.details.validation_failures as Array<{
                                                    source_document_name?: string;
                                                    target_document_name?: string;
                                                    rule_name?: string;
                                                    message?: string;
                                                }>;
                                                const docNames = new Set<string>();
                                                for (const f of failures) {
                                                    if (f.source_document_name) docNames.add(f.source_document_name);
                                                    if (f.target_document_name) docNames.add(f.target_document_name);
                                                }
                                                return Array.from(docNames).map((name) => (
                                                    <span key={name} className="inline-flex px-2 py-0.5 rounded bg-secondary/50 text-xs font-medium text-primary">
                                                        {name}
                                                    </span>
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                )}
                                {/* Missing document types (for missing_document issues) */}
                                {activity.details?.issue_type === 'missing_document' && !!activity.details?.missing_doc_types && Array.isArray(activity.details.missing_doc_types) && (
                                    <div className="mt-2">
                                        <span className="text-tertiary">Documenti mancanti:</span>
                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                            {(activity.details.missing_doc_types as string[]).map((docType) => (
                                                <span key={docType} className="inline-flex px-2 py-0.5 rounded bg-warning-50 text-xs font-medium text-warning-700">
                                                    {docType.replace(/_/g, ' ')}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {/* Cross-check failure details */}
                                {!!activity.details?.validation_failures && Array.isArray(activity.details.validation_failures) && (activity.details.validation_failures as Array<{message?: string}>).length > 0 && (
                                    <div className="mt-2 space-y-1">
                                        <span className="text-tertiary">Dettagli check:</span>
                                        {(activity.details.validation_failures as Array<{rule_name?: string; message?: string; severity?: string}>).slice(0, 3).map((f, i) => (
                                            <div key={i} className="flex items-start gap-2 text-xs pl-2">
                                                <span className={`mt-0.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                                                    f.severity === 'error' || f.severity === 'critical' ? 'bg-error-500' :
                                                    f.severity === 'warning' ? 'bg-warning-500' : 'bg-tertiary'
                                                }`} />
                                                <span>{f.message || f.rule_name || 'Check failed'}</span>
                                            </div>
                                        ))}
                                        {(activity.details.validation_failures as Array<unknown>).length > 3 && (
                                            <span className="text-xs text-tertiary pl-2">+{(activity.details.validation_failures as Array<unknown>).length - 3} altri</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Issue detail (only for container_issue entities) */}
                            {isIssue && (
                                <IssueDetailSection
                                    issueDetail={issueDetail}
                                    isLoading={issueLoading}
                                />
                            )}

                            {/* Inline actions for all open issues */}
                            {isIssue && issueDetail && !isActionDone && (
                                <InlineIssueActions
                                    issueId={activity.entity_id}
                                    containerId={containerId || activity.expedition_id || ''}
                                    currentStatus={issueDetail.resolution_status}
                                />
                            )}

                            {/* Mark as reviewed / unreviewed + Send email for non-issue attention cards */}
                            {isNonIssueAttention && (
                                <div className="mt-3 border-t border-secondary pt-3 flex flex-wrap gap-2">
                                    {isUnread ? (
                                        <Button
                                            size="sm"
                                            color="secondary"
                                            onClick={handleMarkAsReviewed}
                                            isLoading={markAsRead.isPending}
                                            iconLeading={CheckCircle}
                                        >
                                            Mark as reviewed
                                        </Button>
                                    ) : (
                                        <Button
                                            size="sm"
                                            color="secondary"
                                            onClick={handleMarkAsUnreviewed}
                                            isLoading={markAsUnread.isPending}
                                            iconLeading={AlertCircle}
                                        >
                                            Mark as unreviewed
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        onClick={() => setIsEmailModalOpen(true)}
                                        iconLeading={Send01}
                                    >
                                        Send email
                                    </Button>
                                </div>
                            )}

                            {/* Send email for all issue cards */}
                            {isIssue && (
                                <div className="mt-2 flex">
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        onClick={() => setIsEmailModalOpen(true)}
                                        iconLeading={Send01}
                                    >
                                        Send email
                                    </Button>
                                </div>
                            )}

                            {/* Navigation buttons */}
                            <div className="mt-3 border-t border-secondary pt-3 flex flex-wrap gap-2">
                                {/* Issue cards: View Issue (severity-colored) */}
                                {isIssue && (containerId || activity.expedition_id) && (
                                    <a
                                        href={`/shipper/containers/${containerId || activity.expedition_id}?tab=issues`}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                            isHighSeverity
                                                ? 'bg-warning-50 hover:bg-warning-100 text-warning-700'
                                                : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                                        }`}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <AlertCircle className="h-3.5 w-3.5" />
                                        View Issue
                                    </a>
                                )}
                                {/* Issue cards: View Document (specific doc or documents tab for validation issues) */}
                                {isIssue && (containerId || activity.expedition_id) && (
                                    !!activity.details?.document_id ? (
                                        <a
                                            href={`/shipper/containers/${containerId || activity.expedition_id}/documents/${String(activity.details.document_id)}?mode=view`}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <File06 className="h-3.5 w-3.5" />
                                            View Document
                                        </a>
                                    ) : ['single_document_check', 'cross_check_document_check', 'missing_document'].includes(String(activity.details?.issue_type ?? '')) ? (
                                        <a
                                            href={`/shipper/containers/${containerId || activity.expedition_id}?tab=documents`}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <File06 className="h-3.5 w-3.5" />
                                            View Document
                                        </a>
                                    ) : null
                                )}
                                {/* Document activities: View Document (entity is a specific document, or details.document_id available) */}
                                {!isIssue && (
                                    activity.entity_type === 'document' && activity.entity_id ? (
                                        <a
                                            href={containerId
                                                ? `/shipper/containers/${containerId}/documents/${activity.entity_id}?mode=view`
                                                : `/shipper/documents/${activity.entity_id}`
                                            }
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <File06 className="h-3.5 w-3.5" />
                                            View Document
                                        </a>
                                    ) : !!activity.details?.document_id && (containerId || activity.expedition_id) && !(activity.activity_type === 'container_tracked' && String(activity.details?.source) === 'document_extraction') ? (
                                        <a
                                            href={`/shipper/containers/${containerId || activity.expedition_id}/documents/${String(activity.details.document_id)}?mode=view`}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <File06 className="h-3.5 w-3.5" />
                                            View Document
                                        </a>
                                    ) : null
                                )}
                                {/* Container enrichment: View Document or View Mail based on source */}
                                {activity.activity_type === 'container_tracked' && String(activity.details?.source) === 'document_extraction' && !!activity.details?.document_id && (containerId || activity.expedition_id) && (
                                    <a
                                        href={`/shipper/containers/${containerId || activity.expedition_id}/documents/${String(activity.details.document_id)}?mode=view`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <File06 className="h-3.5 w-3.5" />
                                        View Document
                                    </a>
                                )}
                                {['container_tracked', 'status_changed'].includes(activity.activity_type ?? '') && ['email', 'email_body', 'email_extraction'].includes(String(activity.details?.source)) && !!activity.details?.email_message_id && (
                                    <a
                                        href={`/shipper/activity?tab=trade-comms&messageId=${String(activity.details.email_message_id)}`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-brand-50 hover:bg-brand-100 text-brand-700 transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Mail01 className="h-3.5 w-3.5" />
                                        View Mail
                                    </a>
                                )}
                                {/* View Container — only when NOT in container context */}
                                {!isContainerContext && (containerId || activity.expedition_id) && (
                                    <a
                                        href={(() => {
                                            const cid = containerId || activity.expedition_id;
                                            // Note cards → notes tab
                                            if (activity.activity_type === 'note_created') {
                                                return `/shipper/containers/${cid}?tab=notes`;
                                            }
                                            // Document-category cards → documents tab with validation section
                                            if (activity.activity_category === 'document' || activity.activity_type === 'cross_check_executed') {
                                                return `/shipper/containers/${cid}?tab=documents&section=validation`;
                                            }
                                            // Issue cards about documents → documents tab
                                            if (isIssue && ['single_document_check', 'cross_check_document_check', 'missing_document'].includes(String(activity.details?.issue_type ?? ''))) {
                                                return `/shipper/containers/${cid}?tab=documents&section=validation`;
                                            }
                                            // Tracking cards → timeline tab
                                            if (activity.activity_category === 'tracking') {
                                                return `/shipper/containers/${cid}?tab=timeline`;
                                            }
                                            return `/shipper/containers/${cid}`;
                                        })()}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Package className="h-3.5 w-3.5" />
                                        View Container
                                    </a>
                                )}
                                {/* View Mail — for email entity types */}
                                {activity.entity_type === 'email' && activity.entity_id && (
                                    <a
                                        href={`/shipper/activity?tab=trade-comms&messageId=${activity.entity_id}`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-brand-50 hover:bg-brand-100 text-brand-700 transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Mail01 className="h-3.5 w-3.5" />
                                        View Mail
                                    </a>
                                )}
                                {/* Note created: View Note → notes tab */}
                                {activity.activity_type === 'note_created' && (containerId || activity.expedition_id) && (
                                    <a
                                        href={`/shipper/containers/${containerId || activity.expedition_id}?tab=notes`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <MessageTextSquare01 className="h-3.5 w-3.5" />
                                        View Note
                                    </a>
                                )}
                                {/* Document modified: View Document → specific document in view mode */}
                                {activity.activity_type === 'document_modified' && activity.entity_type === 'document' && activity.entity_id && (containerId || activity.expedition_id) && (
                                    <a
                                        href={`/shipper/containers/${containerId || activity.expedition_id}/documents/${activity.entity_id}?mode=view`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Edit05 className="h-3.5 w-3.5" />
                                        View Document
                                    </a>
                                )}
                                {/* Contact extraction: View Address Book */}
                                {activity.activity_type?.toLowerCase() === 'contact_created' && (
                                    <a
                                        href="/shipper/address-book"
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <User01 className="h-3.5 w-3.5" />
                                        View Address Book
                                    </a>
                                )}
                                {/* Contact extraction: View source Email */}
                                {activity.activity_type?.toLowerCase() === 'contact_created' && !!activity.details?.source_email_id && (
                                    <a
                                        href={`/shipper/activity?tab=trade-comms&messageId=${String(activity.details.source_email_id)}`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-brand-50 hover:bg-brand-100 text-brand-700 transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Mail01 className="h-3.5 w-3.5" />
                                        View Email
                                    </a>
                                )}
                                {/* Contact extraction: View source Document */}
                                {activity.activity_type?.toLowerCase() === 'contact_created' && String(activity.details?.source) === 'document_extraction' && !!activity.details?.document_id && (containerId || activity.expedition_id) && (
                                    <a
                                        href={`/shipper/containers/${containerId || activity.expedition_id}/documents/${String(activity.details.document_id)}?mode=view`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <File06 className="h-3.5 w-3.5" />
                                        View Document
                                    </a>
                                )}
                                {/* Company extraction: View Business Partner */}
                                {activity.activity_type?.toLowerCase() === 'company_created' && (
                                    <a
                                        href="/shipper/business-partners"
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <User01 className="h-3.5 w-3.5" />
                                        View Business Partner
                                    </a>
                                )}
                                {/* Company extraction: View source Email */}
                                {activity.activity_type?.toLowerCase() === 'company_created' && !!activity.details?.source_email_id && (
                                    <a
                                        href={`/shipper/activity?tab=trade-comms&messageId=${String(activity.details.source_email_id)}`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-brand-50 hover:bg-brand-100 text-brand-700 transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Mail01 className="h-3.5 w-3.5" />
                                        View Email
                                    </a>
                                )}
                                {/* Company extraction: View source Document */}
                                {activity.activity_type?.toLowerCase() === 'company_created' && String(activity.details?.source) === 'document_extraction' && !!activity.details?.document_id && (containerId || activity.expedition_id) && (
                                    <a
                                        href={`/shipper/containers/${containerId || activity.expedition_id}/documents/${String(activity.details.document_id)}?mode=view`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-secondary hover:bg-tertiary text-primary transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <File06 className="h-3.5 w-3.5" />
                                        View Document
                                    </a>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Email modal — rendered outside the card layout */}
            {showSendEmail && (
                <SendActivityEmailModal
                    isOpen={isEmailModalOpen}
                    onClose={() => setIsEmailModalOpen(false)}
                    activity={activity}
                    containerId={containerId || activity.expedition_id || ''}
                />
            )}
        </div>
    );
}
